import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BUILD_HASH, BUILD_VERSION } from '../../build-info';
import { AppUpdateFailedDialogComponent } from '../../pages/app/shared/components/app-update-failed-dialog/app-update-failed-dialog.component';
import { AuxiliarService } from '../auxiliar.service';
import { AuthService } from '../API/auth/auth.service';

interface DeployedVersionInfo {
  version: string;
  buildHash?: string;
  builtAt?: string;
}

const UPDATE_FLAG_KEY = 'app_update_in_progress';
const UPDATE_DURATION_KEY = 'app_update_duration_ms';
const UPDATE_TARGET_VERSION_KEY = 'app_update_target_version';
const UPDATE_DISMISSED_SERVER_VERSION_KEY = 'app_update_dismissed_server_version';
const UPDATE_FAILED_DIALOG_SHOWN_KEY = 'app_update_failed_dialog_shown';
const UPDATE_PENDING_DIALOG_VERSION_KEY = 'app_update_pending_dialog_version';
const UPDATE_RETURN_URL_KEY = 'app_update_return_url';
const UPDATE_MIN_MS = 2000;
const UPDATE_MAX_MS = 5000;
const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class AppVersionService {

  targetVersion = '';
  updateDurationMs = UPDATE_MIN_MS;
  private checkRunning = false;
  private initialized = false;
  private updateUiStarted = false;
  private readonly versionUrl = 'version.json';

  constructor(
    private http: HttpClient,
    private auxiliarService: AuxiliarService,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
  ) {}

  get currentVersion(): string {
    return BUILD_VERSION;
  }

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    void this.unregisterLegacyServiceWorkers();
    this.checkForUpdate();
    window.setInterval(() => this.checkForUpdate(), DEFAULT_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkForUpdate();
      }
    });
  }

  checkForUpdateOnStartup(): void {
    this.init();
  }

  checkNow(): void {
    this.checkForUpdate();
  }

  async handlePostReloadNavigation(): Promise<void> {
    const returnPath = (sessionStorage.getItem(UPDATE_RETURN_URL_KEY) || '').trim();
    if (!returnPath.startsWith('/dashboard') || !this.authService.isLogged()) {
      return;
    }

    await this.router.navigateByUrl(returnPath, { replaceUrl: true });
  }

  async fetchDeployedVersionInfo(): Promise<DeployedVersionInfo | null> {
    try {
      const response = await fetch(`${this.versionUrl}?t=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      if (!response.ok) {
        return null;
      }

      const data = await response.json() as DeployedVersionInfo;
      const version = this.normalizeVersion(String(data?.version ?? ''));
      if (!version) {
        return null;
      }

      return { ...data, version };
    } catch {
      return null;
    }
  }

  fetchServerVersion(): Observable<string | null> {
    const url = `${this.auxiliarService.getServidor()}/api/settings/appversion?_=${Date.now()}`;
    return this.http.get<{ appversion: string }>(url, {
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      }),
    }).pipe(
      map((result) => this.normalizeVersion(String(result?.appversion ?? ''))),
      catchError(() => of(null)),
    );
  }

  hasUpdateAvailable(
    deployedVersion: string | null,
    deployedBuildHash?: string | null,
    serverVersion?: string | null,
  ): boolean {
    return this.hasDeployedUpdateAvailable(deployedVersion, deployedBuildHash)
      || this.hasServerUpdateAvailable(serverVersion ?? null);
  }

  fetchServerVersionAsync(): Promise<string | null> {
    return firstValueFrom(this.fetchServerVersion());
  }

  requestUpdate(deployedVersion?: string): void {
    sessionStorage.removeItem(UPDATE_DISMISSED_SERVER_VERSION_KEY);
    sessionStorage.removeItem(UPDATE_PENDING_DIALOG_VERSION_KEY);
    this.clearFailedDialogFlags();
    this.updateUiStarted = false;

    void (async () => {
      const requestedServerVersion = deployedVersion && deployedVersion !== '-'
        ? this.normalizeVersion(deployedVersion)
        : null;
      const targetVersion = requestedServerVersion
        || (await this.resolveLatestKnownVersion())
        || this.currentVersion;

      this.startUpdate(targetVersion);
    })();
  }

  requestForceUpdate(): void {
    sessionStorage.removeItem(UPDATE_DISMISSED_SERVER_VERSION_KEY);
    sessionStorage.removeItem(UPDATE_PENDING_DIALOG_VERSION_KEY);
    this.clearFailedDialogFlags();
    this.updateUiStarted = false;
    void this.forceReloadApplication();
  }

  startUpdate(newVersion: string): void {
    if (this.updateUiStarted || this.router.url.startsWith('/actualizando')) {
      return;
    }

    this.updateUiStarted = true;
    this.targetVersion = newVersion;
    this.updateDurationMs = this.getRandomDurationMs();
    sessionStorage.setItem(UPDATE_FLAG_KEY, '1');
    sessionStorage.setItem(UPDATE_DURATION_KEY, String(this.updateDurationMs));
    sessionStorage.setItem(UPDATE_TARGET_VERSION_KEY, newVersion);

    const currentUrl = this.router.url || '';
    if (currentUrl && !currentUrl.startsWith('/actualizando')) {
      sessionStorage.setItem(UPDATE_RETURN_URL_KEY, currentUrl);
    }

    void this.router.navigateByUrl('/actualizando');
  }

  getStoredUpdateDurationMs(): number {
    const stored = Number(sessionStorage.getItem(UPDATE_DURATION_KEY));
    if (stored >= UPDATE_MIN_MS && stored <= UPDATE_MAX_MS) {
      return stored;
    }
    return this.getRandomDurationMs();
  }

  getStoredTargetVersion(): string {
    const stored = String(sessionStorage.getItem(UPDATE_TARGET_VERSION_KEY) || '').trim();
    return stored !== '' ? stored : this.targetVersion || this.currentVersion;
  }

  async performHardReload(): Promise<void> {
    await this.forceReloadApplication();
  }

  showPendingUpdateFailedDialogIfNeeded(): void {
    const pendingVersion = sessionStorage.getItem(UPDATE_PENDING_DIALOG_VERSION_KEY);
    const dismissedVersion = sessionStorage.getItem(UPDATE_DISMISSED_SERVER_VERSION_KEY);
    const deployedVersion = pendingVersion || dismissedVersion;

    if (!deployedVersion) {
      sessionStorage.removeItem(UPDATE_PENDING_DIALOG_VERSION_KEY);
      return;
    }

    void this.fetchUpdateEvaluation().then(({ deployedInfo, serverVersion }) => {
      if (!this.hasUpdateAvailable(
        deployedInfo?.version ?? null,
        deployedInfo?.buildHash ?? null,
        serverVersion,
      )) {
        sessionStorage.removeItem(UPDATE_PENDING_DIALOG_VERSION_KEY);
        return;
      }

      const targetVersion = this.resolveUpdateTargetVersion(deployedInfo, serverVersion)
        ?? this.normalizeVersion(deployedVersion);
      this.tryShowUpdateFailedDialog(targetVersion);
    });
  }

  private checkForUpdate(): void {
    if (this.checkRunning || this.router.url.startsWith('/actualizando')) {
      return;
    }

    this.checkRunning = true;

    void (async () => {
      try {
        await this.checkLegacyVersionSources();
      } finally {
        this.checkRunning = false;
      }
    })();
  }

  private async checkLegacyVersionSources(): Promise<void> {
    const justReloaded = sessionStorage.getItem(UPDATE_FLAG_KEY) === '1';
    if (justReloaded) {
      sessionStorage.removeItem(UPDATE_FLAG_KEY);
    }

    const { deployedInfo, serverVersion } = await this.fetchUpdateEvaluation();
    if (!this.hasUpdateAvailable(
      deployedInfo?.version ?? null,
      deployedInfo?.buildHash ?? null,
      serverVersion,
    )) {
      this.clearUpdateState();
      return;
    }

    const targetVersion = this.resolveUpdateTargetVersion(deployedInfo, serverVersion);
    if (!targetVersion) {
      this.clearUpdateState();
      return;
    }

    const normalizedTargetVersion = this.normalizeVersion(targetVersion);
    const dismissedVersion = sessionStorage.getItem(UPDATE_DISMISSED_SERVER_VERSION_KEY);
    if (dismissedVersion === normalizedTargetVersion) {
      this.queueUpdateFailedDialog(normalizedTargetVersion);
      return;
    }

    if (justReloaded) {
      sessionStorage.setItem(UPDATE_DISMISSED_SERVER_VERSION_KEY, normalizedTargetVersion);
      this.queueUpdateFailedDialog(normalizedTargetVersion);
      return;
    }

    this.startUpdate(normalizedTargetVersion);
  }

  async forceReloadApplication(): Promise<void> {
    const targetPath = this.resolveReloadTargetPath();
    sessionStorage.setItem(UPDATE_RETURN_URL_KEY, targetPath);
    sessionStorage.setItem(UPDATE_FLAG_KEY, '1');

    await this.clearApplicationCaches();
    const cacheBust = Date.now();
    const baseHref = (document.querySelector('base')?.getAttribute('href') || '/').replace(/\/?$/, '/');
    window.location.replace(`${window.location.origin}${baseHref}?cb=${cacheBust}`);
  }

  private async verifyUpdateSucceeded(): Promise<boolean> {
    const { deployedInfo, serverVersion } = await this.fetchUpdateEvaluation();
    return !this.hasUpdateAvailable(
      deployedInfo?.version ?? null,
      deployedInfo?.buildHash ?? null,
      serverVersion,
    );
  }

  private async clearApplicationCaches(): Promise<void> {
    if (!('caches' in window)) {
      return;
    }

    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch {
      // Ignore cache API errors and continue with reload.
    }
  }

  private async unregisterLegacyServiceWorkers(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length === 0) {
        return;
      }

      await Promise.all(registrations.map((registration) => registration.unregister()));
      await this.clearApplicationCaches();
    } catch {
      // Ignore legacy SW cleanup errors.
    }
  }

  private resolveReloadTargetPath(): string {
    if (this.authService.isLogged()) {
      const saved = (sessionStorage.getItem(UPDATE_RETURN_URL_KEY) || '').trim();
      if (saved.startsWith('/dashboard') && !saved.startsWith('/actualizando')) {
        return saved;
      }
      return '/dashboard/home';
    }

    return '/login';
  }

  private normalizeVersion(version: string): string {
    return version.trim();
  }

  private queueUpdateFailedDialog(deployedVersion: string): void {
    sessionStorage.setItem(UPDATE_PENDING_DIALOG_VERSION_KEY, deployedVersion);
    this.tryShowUpdateFailedDialog(deployedVersion);
  }

  private clearUpdateState(): void {
    sessionStorage.removeItem(UPDATE_DISMISSED_SERVER_VERSION_KEY);
    sessionStorage.removeItem(UPDATE_PENDING_DIALOG_VERSION_KEY);
    sessionStorage.removeItem(UPDATE_RETURN_URL_KEY);
    this.clearFailedDialogFlags();
    this.updateUiStarted = false;
  }

  private async fetchUpdateEvaluation(): Promise<{
    deployedInfo: DeployedVersionInfo | null;
    serverVersion: string | null;
  }> {
    const [deployedInfo, serverVersion] = await Promise.all([
      this.fetchDeployedVersionInfo(),
      this.fetchServerVersionAsync().catch(() => null),
    ]);

    return { deployedInfo, serverVersion };
  }

  private async resolveLatestKnownVersion(): Promise<string | null> {
    const { deployedInfo, serverVersion } = await this.fetchUpdateEvaluation();
    return this.resolveUpdateTargetVersion(deployedInfo, serverVersion)
      || deployedInfo?.version
      || serverVersion
      || null;
  }

  private hasDeployedUpdateAvailable(
    deployedVersion: string | null,
    deployedBuildHash?: string | null,
  ): boolean {
    if (!deployedVersion) {
      return false;
    }

    const normalized = this.normalizeVersion(deployedVersion);
    if (normalized === this.currentVersion) {
      const remoteHash = (deployedBuildHash || '').trim();
      const localHash = (BUILD_HASH || '').trim();
      return Boolean(remoteHash && localHash && remoteHash !== localHash);
    }

    return this.isServerVersionNewer(normalized, this.currentVersion);
  }

  private hasServerUpdateAvailable(serverVersion: string | null): boolean {
    if (!serverVersion) {
      return false;
    }

    const normalized = this.normalizeVersion(serverVersion);
    return this.isServerVersionNewer(normalized, this.currentVersion);
  }

  private resolveUpdateTargetVersion(
    deployedInfo: DeployedVersionInfo | null,
    serverVersion: string | null,
  ): string | null {
    const candidates: string[] = [];

    if (deployedInfo?.version && this.hasDeployedUpdateAvailable(deployedInfo.version, deployedInfo.buildHash)) {
      candidates.push(deployedInfo.version);
    }

    if (serverVersion && this.hasServerUpdateAvailable(serverVersion)) {
      candidates.push(serverVersion);
    }

    if (candidates.length === 0) {
      return null;
    }

    return candidates.reduce((best, current) => (
      this.isServerVersionNewer(current, best) ? current : best
    ));
  }

  private tryShowUpdateFailedDialog(deployedVersion: string): void {
    if (!this.authService.isLogged()) {
      return;
    }

    const shownKey = `${UPDATE_FAILED_DIALOG_SHOWN_KEY}:${deployedVersion}`;
    if (sessionStorage.getItem(shownKey) === '1') {
      return;
    }

    sessionStorage.setItem(shownKey, '1');
    sessionStorage.removeItem(UPDATE_PENDING_DIALOG_VERSION_KEY);
    sessionStorage.removeItem(UPDATE_RETURN_URL_KEY);

    this.dialog.open(AppUpdateFailedDialogComponent, {
      data: {
        serverVersion: deployedVersion,
        clientVersion: this.currentVersion,
      },
      width: '90%',
      maxWidth: '480px',
    }).afterClosed().subscribe((result) => {
      if (result?.action === 'force') {
        this.requestForceUpdate();
        return;
      }
      if (result?.action === 'help') {
        this.router.navigateByUrl('/dashboard/help');
      }
    });
  }

  private clearFailedDialogFlags(): void {
    const prefix = `${UPDATE_FAILED_DIALOG_SHOWN_KEY}:`;
    for (let index = sessionStorage.length - 1; index >= 0; index--) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(prefix)) {
        sessionStorage.removeItem(key);
      }
    }
  }

  private isServerVersionNewer(serverVersion: string, clientVersion: string): boolean {
    const serverParts = serverVersion.split('.').map((part) => Number(part) || 0);
    const clientParts = clientVersion.split('.').map((part) => Number(part) || 0);
    const length = Math.max(serverParts.length, clientParts.length);

    for (let index = 0; index < length; index++) {
      const serverPart = serverParts[index] ?? 0;
      const clientPart = clientParts[index] ?? 0;

      if (serverPart > clientPart) {
        return true;
      }
      if (serverPart < clientPart) {
        return false;
      }
    }

    return false;
  }

  private getRandomDurationMs(): number {
    return Math.floor(Math.random() * (UPDATE_MAX_MS - UPDATE_MIN_MS + 1)) + UPDATE_MIN_MS;
  }

}
